/**
 * Operator-facing formatters for crop-steering UI.
 * Wraps shared/drybackUnits math — does not change controller wording.
 *
 * Kind-based dual display:
 * - dryback → relative primary + ≈ pts + ≤ trigger VWC
 * - overnight → (ref−vwc)/peak primary + ≈ pts + target/trigger
 * - trigger → abs VWC primary + band % secondary
 * - live VWC → abs primary + optional vs-peak dryback
 * - peak/FC/floor → abs only (no helper needed)
 */
import {
  absolutePtsFromRelative,
  overnightDrybackPercent,
  overnightPtsDrop,
  relativeDrybackPercent,
  relativeFromTriggerVwc,
  triggerVwcFromRelative,
  type DrybackRefName,
} from '../../shared/drybackUnits';
import type { DecisionCondition } from '../../shared/types/IrrigationRealTime';

export function formatVwc(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(digits)}%`;
}

export function formatEc(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(digits);
}

export function formatMl(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${Math.round(v)} mL`;
}

export function formatPts(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(digits)} pts`;
}

export interface RelDrybackParts {
  /** Numeric display e.g. "18%" */
  value: string;
  /** Unit label next to value: "of peak" */
  unit: string;
  /** Sub-line e.g. "≈ 5.2 pts · ≤ 55.8% VWC · target 10%" */
  sub?: string;
  /** Raw relative % for math consumers */
  pct: number | null;
}

/**
 * Relative dryback display: value "18%" + unit "of peak".
 * When `ref` (or legacy `peak`) is known, always attach live ≈ pts.
 * Target/band + trigger go in the sub when targetPct is set.
 */
export function formatRelDryback(
  pct: number | null | undefined,
  opts?: {
    digits?: number;
    of?: DrybackRefName;
    /** Absolute VWC reference for pts/trigger (peak, saturation, FC, …) */
    ref?: number | null;
    /** @deprecated use `ref` */
    peak?: number | null;
    targetPct?: number | null;
    /** Prefix for the sub line (e.g. "target", "band", "last night · target") */
    subPrefix?: string;
    includeTrigger?: boolean;
    /** When true, omit live pts for the displayed pct (band-only sub). */
    skipLivePts?: boolean;
  },
): RelDrybackParts {
  const digits = opts?.digits ?? 0;
  const of = opts?.of ?? 'peak';
  const unit = `of ${of}`;
  const ref = opts?.ref ?? opts?.peak ?? null;

  if (pct == null || !Number.isFinite(pct)) {
    return { value: '—', unit, pct: null, sub: formatDrybackSub({ ...opts, ref, livePct: null }) };
  }

  const value = `${pct.toFixed(digits)}%`;
  const sub = formatDrybackSub({ ...opts, ref, livePct: pct });
  return { value, unit, pct, sub };
}

function formatDrybackSub(opts: {
  digits?: number;
  ref?: number | null;
  peak?: number | null;
  targetPct?: number | null;
  subPrefix?: string;
  includeTrigger?: boolean;
  skipLivePts?: boolean;
  livePct?: number | null;
}): string | undefined {
  const d = opts.digits ?? 0;
  const ref = opts.ref ?? opts.peak ?? null;
  const parts: string[] = [];
  const target = opts.targetPct;
  const hasTarget = target != null && Number.isFinite(target) && target > 0;

  // Contextual prefix (e.g. "predicted", "idle") when not a target label
  if (opts.subPrefix && !hasTarget) {
    parts.push(opts.subPrefix);
  }

  // Live absolute pts for the displayed dryback %
  if (
    !opts.skipLivePts
    && opts.livePct != null
    && Number.isFinite(opts.livePct)
    && ref != null
    && ref > 0
  ) {
    parts.push(`≈ ${absolutePtsFromRelative(ref, opts.livePct).toFixed(1)} pts`);
  }

  if (hasTarget) {
    // subPrefix is the label for the target line ("band", "target", "predicted · target")
    const prefix = opts.subPrefix || 'target';
    parts.push(`${prefix} ${target!.toFixed(d)}%`);
    if (ref != null && ref > 0) {
      // When live pts already shown, only add trigger for the target band
      if (opts.skipLivePts || opts.livePct == null) {
        parts.push(`≈ ${absolutePtsFromRelative(ref, target!).toFixed(1)} pts`);
      }
      if (opts.includeTrigger !== false) {
        parts.push(`≤ ${triggerVwcFromRelative(ref, target!).toFixed(1)}% VWC`);
      }
    }
  } else if (
    opts.includeTrigger
    && opts.skipLivePts
    && opts.livePct != null
    && opts.livePct > 0
    && ref != null
    && ref > 0
  ) {
    // Band-as-value: show trigger for that band
    parts.push(`≈ ${absolutePtsFromRelative(ref, opts.livePct).toFixed(1)} pts`);
    parts.push(`≤ ${triggerVwcFromRelative(ref, opts.livePct).toFixed(1)}% VWC`);
  }

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** Live relative dryback % of a reference (peak/FC). */
export function liveRelDrybackPct(ref: number | null | undefined, vwc: number | null | undefined): number | null {
  if (ref == null || !(ref > 0) || vwc == null || !Number.isFinite(vwc)) return null;
  return relativeDrybackPercent(ref, vwc);
}

/** Band / target line: "band 5% · ≈ 3.1 pts · ≤ 58.6% VWC" */
export function formatRelBand(
  target: number,
  ref: number | null,
  label = 'band',
  of: DrybackRefName = 'last shot',
): string {
  if (!(target > 0)) return '';
  const parts = formatRelDryback(target, {
    digits: 0,
    of,
    ref,
    peak: ref,
    targetPct: target,
    subPrefix: label,
    includeTrigger: true,
    skipLivePts: true,
  });
  return parts.sub ?? `${label} ${target.toFixed(0)}% of ${of}`;
}

/** Inline dryback for prose: "10% of peak (≈ 5.4 pts · ≤ 48.6% VWC)" */
export function formatDualDrybackInline(
  relativePct: number,
  ref?: number | null,
  opts?: { digits?: number; of?: DrybackRefName; includeTrigger?: boolean },
): string {
  const d = opts?.digits ?? 0;
  const of = opts?.of ?? 'peak';
  const pct = Number.isFinite(relativePct) ? relativePct : 0;
  const head = `${pct.toFixed(d)}% of ${of}`;
  if (ref == null || !(ref > 0)) return head;
  const bits = [`≈ ${absolutePtsFromRelative(ref, pct).toFixed(1)} pts`];
  if (opts?.includeTrigger !== false) {
    bits.push(`≤ ${triggerVwcFromRelative(ref, pct).toFixed(1)}% VWC`);
  }
  return `${head} (${bits.join(' · ')})`;
}

/** Inline trigger: "≤ 48.6% VWC (≈ 10% of peak)" */
export function formatDualTriggerInline(
  triggerVwc: number,
  opts?: {
    bandPct?: number | null;
    peak?: number | null;
    of?: DrybackRefName;
    digits?: number;
  },
): string {
  const of = opts?.of ?? 'peak';
  const d = opts?.digits ?? 0;
  const base = `≤ ${triggerVwc.toFixed(1)}% VWC`;
  let band = opts?.bandPct;
  if ((band == null || !Number.isFinite(band)) && opts?.peak != null && opts.peak > 0) {
    band = relativeFromTriggerVwc(opts.peak, triggerVwc);
  }
  if (band != null && Number.isFinite(band) && band > 0) {
    return `${base} (≈ ${band.toFixed(d)}% of ${of})`;
  }
  return base;
}

export interface OvernightDrybackParts extends RelDrybackParts {
  refVwc: number | null;
  peak: number | null;
}

/**
 * Overnight progress/last/predicted — primary is % of last irrigation ref
 * (last P2 shot, predictive stop, or manual/external rehydration),
 * secondary ≈ pts from (ref−vwc) + target/trigger vs ref.
 */
export function formatOvernightDrybackDisplay(opts: {
  pct?: number | null;
  refVwc?: number | null;
  vwc?: number | null;
  peak?: number | null;
  targetPct?: number | null;
  subPrefix?: string;
  digits?: number;
}): OvernightDrybackParts {
  const digits = opts.digits ?? 0;
  const peak = opts.peak != null && opts.peak > 0 ? opts.peak : null;
  const refVwc = opts.refVwc != null && opts.refVwc > 0 ? opts.refVwc : null;
  let pct = opts.pct;
  if ((pct == null || !Number.isFinite(pct)) && refVwc != null && opts.vwc != null) {
    pct = overnightDrybackPercent(refVwc, opts.vwc);
  }
  const unit = 'of last irrigation';
  if (pct == null || !Number.isFinite(pct)) {
    const sub = overnightSub({ ref: refVwc ?? peak, targetPct: opts.targetPct, subPrefix: opts.subPrefix, digits });
    return { value: '—', unit, pct: null, sub, refVwc, peak };
  }
  const parts: string[] = [];
  if (opts.subPrefix) parts.push(opts.subPrefix);
  if (refVwc != null && opts.vwc != null && Number.isFinite(opts.vwc)) {
    parts.push(`≈ ${overnightPtsDrop(refVwc, opts.vwc).toFixed(1)} pts`);
  } else if (refVwc != null) {
    parts.push(`≈ ${absolutePtsFromRelative(refVwc, pct).toFixed(1)} pts`);
  }
  const targetBits = overnightSub({ ref: refVwc ?? peak, targetPct: opts.targetPct, subPrefix: undefined, digits, asPartsOnly: true });
  if (targetBits) parts.push(targetBits);
  return { value: `${Math.max(0, pct).toFixed(digits)}%`, unit, pct, sub: parts.length ? parts.join(' · ') : undefined, refVwc, peak };
}

function overnightSub(opts: {
  ref?: number | null;
  targetPct?: number | null;
  subPrefix?: string;
  digits: number;
  asPartsOnly?: boolean;
}): string | undefined {
  const parts: string[] = [];
  if (opts.subPrefix && !opts.asPartsOnly) parts.push(opts.subPrefix);
  const target = opts.targetPct;
  const ref = opts.ref != null && opts.ref > 0 ? opts.ref : null;
  if (target != null && Number.isFinite(target) && target > 0) {
    const bits = [`target ${target.toFixed(opts.digits)}% of last irrigation`];
    if (ref != null) {
      bits.push(`≈ ${absolutePtsFromRelative(ref, target).toFixed(1)} pts`);
      bits.push(`≤ ${triggerVwcFromRelative(ref, target).toFixed(1)}% VWC`);
    }
    parts.push(bits.join(' · '));
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export interface DrybackTileDisplay {
  /** Absolute VWC reference the tile measures from; null → caller keeps its peak fallback. */
  refVwc: number | null;
  /** Unit label: "of peak" / "of last irrigation". */
  refName: DrybackRefName;
  /** Target/band % against that ref (drives the target + trigger hint). */
  targetPct: number | null;
}

/**
 * Resolve the chart-header Dryback tile reference.
 * Establishment gate active: the persistent transplant reference (E1 =
 * saturation `vwcAtRunoff`, E2 = last P1 peak) — survives every day roll
 * until the gate opens; crest/overnight refs reset daily and must not hide it.
 * Active P1/P2: last irrigation crest (between-shots).
 * After P2 stop / overnight: overnightDrybackRefVwc with overnight target.
 * Day peak is never the dryback reference.
 */
export function resolveDrybackTileDisplay(args: {
  overnightInProgress: boolean;
  runtimeStale: boolean;
  phase?: string | null;
  /** Last irrigation crest (preferred) — not day peak. */
  crestVwc?: number | null;
  /** @deprecated use crestVwc */
  peakVwc?: number | null;
  /** runtime.overnightDrybackRefVwc — VWC at the last irrigation. */
  overnightRefVwc?: number | null;
  p1TargetPct?: number | null;
  p2BandPct?: number | null;
  overnightTargetPct?: number | null;
  /** Transplant establishment gate (E1/E2) — takes precedence when active. */
  establishment?: {
    gateActive: boolean;
    /** E1: vwcAtRunoff (saturation). E2: lastP1PeakVwc. */
    refVwc?: number | null;
    /** E1: establishmentDrybackTarget (35–40%). E2: reEstablishDrybackTarget. */
    targetPct?: number | null;
    refName: DrybackRefName;
  } | null;
}): DrybackTileDisplay {
  const crest = (args.crestVwc != null && args.crestVwc > 0)
    ? args.crestVwc
    : (args.peakVwc != null && args.peakVwc > 0 ? args.peakVwc : null);
  const p2Band = args.p2BandPct != null && Number.isFinite(args.p2BandPct) ? args.p2BandPct : null;

  // Establishment reference comes from program targets (not runtime), so it is
  // valid even when runtime telemetry is stale.
  const est = args.establishment;
  if (est?.gateActive) {
    return {
      refVwc: est.refVwc != null && est.refVwc > 0 ? est.refVwc : null,
      refName: est.refName,
      targetPct: est.targetPct != null && Number.isFinite(est.targetPct) && est.targetPct > 0
        ? est.targetPct
        : null,
    };
  }

  if (args.runtimeStale) {
    return { refVwc: null, refName: 'last shot', targetPct: p2Band };
  }
  if (args.overnightInProgress) {
    const ref = args.overnightRefVwc != null && args.overnightRefVwc > 0
      ? args.overnightRefVwc
      : crest;
    return {
      refVwc: ref,
      refName: 'last irrigation',
      targetPct: args.overnightTargetPct != null && Number.isFinite(args.overnightTargetPct)
        ? args.overnightTargetPct
        : null,
    };
  }
  if (args.phase === 'P1') {
    return {
      refVwc: crest,
      refName: 'last shot',
      targetPct: args.p1TargetPct != null && Number.isFinite(args.p1TargetPct) ? args.p1TargetPct : null,
    };
  }
  return { refVwc: crest, refName: 'last shot', targetPct: p2Band };
}

/** Live VWC with optional vs-FC secondary (not day-peak dryback). */
export function formatLiveVwcDisplay(opts: {
  vwc: number | null | undefined;
  /** @deprecated ignored for CONTROL display — use fc */
  peak?: number | null;
  /** Learned field capacity (effectiveFC). */
  fc?: number | null;
  /** True when fc is a manual operator override, not a learned value. */
  fcManual?: boolean;
  /** Program FC seed (fieldCapacityTarget) — shown only when it differs from learned FC. */
  fcTarget?: number | null;
  extraSub?: string;
  digits?: number;
}): { value: string; unit?: string; sub?: string } {
  const d = opts.digits ?? 1;
  if (opts.vwc == null || !Number.isFinite(opts.vwc)) {
    return { value: '—', sub: opts.extraSub };
  }
  const parts: string[] = [];
  if (opts.fc != null && Number.isFinite(opts.fc) && opts.fc > 0) {
    const rel = relativeDrybackPercent(opts.fc, opts.vwc);
    const pts = Math.max(0, opts.fc - opts.vwc);
    parts.push(`≈ ${rel.toFixed(0)}% vs FC`);
    parts.push(`≈ ${pts.toFixed(1)} pts`);
    parts.push(`FC ${opts.fc.toFixed(0)}%${opts.fcManual ? ' manual' : ''}`);
  }
  if (
    opts.fcTarget != null
    && Number.isFinite(opts.fcTarget)
    && (opts.fc == null || !Number.isFinite(opts.fc) || Math.abs(opts.fcTarget - opts.fc) > 1)
  ) {
    parts.push(`seed ${opts.fcTarget.toFixed(0)}%`);
  }
  if (opts.extraSub) parts.push(opts.extraSub);
  return {
    value: opts.vwc.toFixed(d),
    unit: '%',
    sub: parts.length > 0 ? parts.join(' · ') : undefined,
  };
}

/** Branch executeCondition on type — never label dryback as VWC. */
export function formatExecuteCondition(
  condition: DecisionCondition | null | undefined,
  opts?: { peak?: number | null },
): string | null {
  if (!condition) return null;
  const v = condition.value;
  if (!Number.isFinite(v)) return null;
  switch (condition.type) {
    case 'dryback_percent': {
      const crest = opts?.peak;
      if (crest != null && crest > 0) {
        return `when dryback ≥ ${formatDualDrybackInline(v, crest, { digits: 0, of: 'last shot' })}`;
      }
      return `when dryback ≥ ${v.toFixed(0)}% of last shot`;
    }
    case 'vwc_below': {
      const crest = opts?.peak;
      if (crest != null && crest > 0) {
        const band = relativeFromTriggerVwc(crest, v);
        const bandNote = band != null && band > 0 ? ` (≈ ${band.toFixed(0)}% of last shot)` : '';
        return `when VWC < ${v.toFixed(1)}%${bandNote}`;
      }
      return `when VWC < ${v.toFixed(1)}%`;
    }
    case 'time':
      return `after ${v}s`;
    default:
      return `when condition ${v}`;
  }
}
